<template>
  <div>
    <div
      class="ratio-16-9 mb-6 bg-full bg-grey-1"
      :style="`background-image: url(${image.src})`"
    >
      <span role="img" :alt="image.alt" />
      <h1
        id="focus-target"
        class="absolute bottom-0 w-full p-x py-2 focus-b"
        :class="[`bg-${palette.primary}`]"
        v-html="category.title"
      />
    </div>

    <div class="p-x">
      <h2
        v-if="!phone"
        :class="[`border-${palette.primary}`]"
        v-html="category.heading"
      />

      <div class="flex flex-col md:flex-row">
        <div class="md:w-1/2" :class="{ 'order-2': phone }">
          <h2
            v-if="phone"
            :class="[`border-${palette.primary}`]"
            v-html="category.heading"
          />
          <p v-html="category.description" />
          <a
            v-if="category.href"
            :href="utmHref(category.href, 'tier-2-cta')"
            target="_blank"
            class="inline-block mt-2 text-primary font-black underline focus-a"
            v-html="'Find out more'"
          />
        </div>

        <nav
          aria-label="Content"
          class="md:w-1/2 md:ml-4"
          :class="{ 'order-1 mb-6': phone }"
        >
          <ul>
            <li
              v-for="item in category.children"
              :key="item.id"
              class="mb-1 border-b"
            >
              <RouterLink
                :to="path(item)"
                :event="item.type === 'location' ? '' : 'click'"
                class="flex py-1 focus-a"
                :data-gtm-category-sidebar-item="
                  `Category: ${category.title}, Title: ${item.title}, Type: ${item.type}`
                "
                @click.native="handleClick(item)"
                @focus.native="handleFocus(item)"
                @blur.native="handleBlur(item)"
                @keyup.native.enter="handleEnter(item)"
                @keyup.native.escape="handleEscape(item)"
              >
                <IconBase
                  :icon="`${item.type}icon`"
                  size="1.1rem"
                  color="primary"
                  class="mr-3 self-center"
                />
                {{ htmlDecode(item.title) }}
              </RouterLink>
            </li>
          </ul>
        </nav>
      </div>

      <blockquote v-if="category.quote" class="py-6">
        <img
          v-if="avatar"
          :src="avatar.src"
          :alt="avatar.alt"
          class="h-16 w-16 mr-2 rounded-full float-left"
        />
        <p class="clear-none relative mb-3 leading-normal font-serif">
          <svg xmlns="http://www.w3.org/2000/svg" width="37" height="32" viewBox="0 0 37 32" v-bind:svg-inline="''" v-bind:class="'absolute top-0 -mt-4 -ml-4 h-10 w-10'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="#e3e3e3" d="M19.697 11.793a10.1 10.1 0 00-7.964 2.586l.008-.007c2.137-4.326 6.354-7.338 11.31-7.735l.049-.003.029-.251c-7.427 0-13.998 4.642-14.834 11.86-.056.453-.088.978-.088 1.51 0 2.053.476 3.995 1.324 5.721l-.034-.076c.112.241.236.477.365.708 1.329 2.273 3.476 3.946 6.028 4.628l.069.016c.042.011.085.02.128.03.385.102.854.189 1.333.244l.047.004c.361.047.778.073 1.202.073 5.012 0 9.155-3.724 9.811-8.556l.006-.051a9.51 9.51 0 00.051-.993c0-5.064-3.862-9.227-8.802-9.703l-.04-.003z"/></svg>
          <span class="relative z-1" v-html="category.quote.copy" />
          <span class="relative inline-block">
            <svg xmlns="http://www.w3.org/2000/svg" width="37" height="32" viewBox="0 0 37 32" v-bind:svg-inline="''" v-bind:class="'absolute bottom-0 -mb-6 -ml-6 h-10 w-10 rotate-180'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="#e3e3e3" d="M19.697 11.793a10.1 10.1 0 00-7.964 2.586l.008-.007c2.137-4.326 6.354-7.338 11.31-7.735l.049-.003.029-.251c-7.427 0-13.998 4.642-14.834 11.86-.056.453-.088.978-.088 1.51 0 2.053.476 3.995 1.324 5.721l-.034-.076c.112.241.236.477.365.708 1.329 2.273 3.476 3.946 6.028 4.628l.069.016c.042.011.085.02.128.03.385.102.854.189 1.333.244l.047.004c.361.047.778.073 1.202.073 5.012 0 9.155-3.724 9.811-8.556l.006-.051a9.51 9.51 0 00.051-.993c0-5.064-3.862-9.227-8.802-9.703l-.04-.003z"/></svg>
          </span>
        </p>
        <footer
          v-if="category.quote.source || category.quote.qualification"
          class="font-bold"
        >
          <span
            v-if="category.quote.source"
            class="inline-block mb-1 border-b border-gold leading-none"
            v-html="category.quote.source"
          />
          <br />
          <span
            v-if="category.quote.qualification"
            class="inline-block border-b border-gold leading-none"
            v-html="category.quote.qualification"
          />
        </footer>
      </blockquote>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { bus } from '@/main'
import IconBase from '@/components/icons/IconBase.vue'

export default {
  name: 'Category',

  components: {
    IconBase
  },

  props: {
    category: {
      type: Object,
      default: () => {}
    }
  },

  computed: {
    ...mapState(['touch', 'mobile', 'phone', 'browser']),
    ...mapGetters([]),

    palette() {
      return this.category.palette
    },

    image() {
      if (this.category.image)
        return {
          src: this.category.image.sizes.medium,
          alt: this.category.image.alt
        }
      return ''
    },

    avatar() {
      if (this.category.quote?.avatar)
        return {
          src: this.category.quote.avatar.sizes.thumbnail,
          alt: this.category.quote.avatar.alt
        }
      return ''
    }
  },

  methods: {
    path(item) {
      if (item.type === 'location')
        return {
          name: 'campus-panorama',
          params: {
            category_id: this.category.slug,
            media_id: item.slug,
            panorama_id: item.children[0].slug
          }
        }
      return {
        name: 'campus-media',
        params: { category_id: this.category.slug, media_id: item.slug }
      }
    },

    handleFocus(item) {
      if (item.type === 'location') {
        // Event bus direct to Panorama.vue
        bus.$emit('panToHotSpot', item.slug)
      }
    },

    handleBlur(item) {
      // Event bus direct to Panorama.vue
      if (item.type === 'location') {
        bus.$emit('closeHotSpot', item.slug)
      }
    },

    handleEscape(item) {
      // Event bus direct to Panorama.vue
      if (item.type === 'location') {
        bus.$emit('closeHotSpot', item.slug)
      }
    },

    handleEnter(item) {
      if (item.type === 'location') {
        // bus.$emit('hotSpotClick', item.slug)
        this.$router.push(this.path(item))
      }
    },

    handleClick(item) {
      if (item.type === 'location' && this.mobile) {
        this.$router.push(this.path(item))
      }

      // Because Safari doesn't give focus on click
      if (
        item.type === 'location' &&
        !this.mobile &&
        this.browser === 'Safari'
      ) {
        this.handleFocus(item)
      }
    }
  }
}
</script>
